// src/hooks/useIonoNowPlaying.ts
import { useEffect, useState } from "react";

export type IonoNowPlayingRawItem = {
    v?: number;
    metadata?: {
        contentType?: string;
        duration?: number;
        title?: string;        // track title
        creator?: string;      // artist
        publishDate?: string;  // ISO date-time
        event?: string;        // sometimes present
    };
};

export type IonoNowPlaying = {
    artist?: string;   // mapped from creator
    title?: string;
    dt?: string;       // mapped from publishDate
    duration?: number;
    event?: string;
    raw?: IonoNowPlayingRawItem; // first raw item for debugging
};

export function useIonoNowPlaying(streamId: number | string, intervalMs = 10_000) {
    const [data, setData] = useState<IonoNowPlaying | null>(null);

    useEffect(() => {
        let dead = false;
        let timer: number | undefined;

        const load = async () => {
            const ctrl = new AbortController();
            try {
                const r = await fetch(`https://edge.iono.fm/nowplaying/${streamId}`, {
                    cache: "no-store",
                    signal: ctrl.signal,
                });
                if (!r.ok) throw new Error(String(r.status));
                const arr: IonoNowPlayingRawItem[] = await r.json();

                const first = Array.isArray(arr) && arr.length ? arr[0] : undefined;
                const m = first?.metadata ?? {};

                const normalized: IonoNowPlaying = {
                    artist: m.creator || undefined,
                    title: m.title || undefined,
                    dt: m.publishDate || undefined,
                    duration: typeof m.duration === "number" ? m.duration : undefined,
                    event: (m as any).event || undefined,
                    raw: first,
                };

                if (!dead) setData(normalized);
            } catch {
                if (!dead) setData(null);
            }
            return () => ctrl.abort();
        };

        // initial + polling
        load();
        timer = window.setInterval(load, intervalMs);

        return () => {
            dead = true;
            if (timer) window.clearInterval(timer);
        };
    }, [streamId, intervalMs]);

    return data;
}
